import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Settings, Clock, ListOrdered, X, RefreshCw, Users, Calendar, AlertTriangle, CheckCircle, Ban } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Automation() {
  const [settings, setSettings] = useState(null);
  const [queue, setQueue] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [excludingId, setExcludingId] = useState(null);
  const [formData, setFormData] = useState({
    auto_send_enabled: true,
    send_time: '09:00'
  });

  useEffect(() => {
    fetchSettings();
    fetchQueue();
    fetchPreview();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchQueue();
      fetchPreview();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/automation/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setFormData({
        auto_send_enabled: response.data.auto_send_enabled,
        send_time: response.data.send_time
      });
    } catch (error) {
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const fetchQueue = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/automation/queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQueue(response.data);
    } catch (error) {
      console.error('Erro ao carregar fila:', error);
    }
  };

  const fetchPreview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/automation/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreview(response.data);
    } catch (error) {
      console.error('Erro ao carregar previsão:', error);
    }
  };

  const excludeClient = async (clientId, clientName) => {
    if (!window.confirm(`Tem certeza que deseja cancelar o envio para ${clientName} hoje?`)) {
      return;
    }

    setExcludingId(clientId);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/automation/exclude/${clientId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Envio cancelado para ${clientName}`);
      fetchPreview();
    } catch (error) {
      toast.error('Erro ao cancelar envio');
    } finally {
      setExcludingId(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/automation/settings?auto_send_enabled=${formData.auto_send_enabled}&send_time=${formData.send_time}&auto_renewal_enabled=false`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Configurações salvas!');
      fetchSettings();
      fetchPreview();
    } catch (error) {
      toast.error('Erro ao salvar configurações');
    }
  };

  const getStatusBadge = (daysDiff) => {
    if (daysDiff < 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-rose-500/10 text-rose-400 border border-rose-500/30">
          <AlertTriangle className="w-3 h-3" />
          Atrasado
        </span>
      );
    } else if (daysDiff === 0) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-500/10 text-amber-400 border border-amber-500/30">
          <Calendar className="w-3 h-3" />
          Vence Hoje
        </span>
      );
    } else {
      return (
        <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30">
          <CheckCircle className="w-3 h-3" />
          Lembrete
        </span>
      );
    }
  };

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="automation-page">
      <Sidebar />
      
      <main className="flex-1 md:ml-64 p-4 md:p-6 lg:p-8">
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Automação</h1>
          <p className="text-slate-400">Configure envios automáticos de mensagens</p>
        </div>

        {/* Aviso sobre Renovações */}
        <div className="mb-6 bg-violet-900/20 border border-violet-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-violet-500/20 rounded-lg">
              <Settings className="w-5 h-5 text-violet-400" />
            </div>
            <div>
              <h3 className="font-semibold text-violet-300 mb-1">ℹ️ Sobre Renovações de Clientes</h3>
              <p className="text-sm text-slate-300">
                Renovações automáticas estão <strong>desabilitadas</strong>. Todos os clientes devem ser renovados manualmente através do botão "Renovar" no card de cada cliente.
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-400">Carregando...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Configurações */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Settings className="w-6 h-6 mr-3 text-violet-500" />
                Configurações de Automação
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg">
                    <div>
                      <Label className="text-white text-base">Envio Automático de Mensagens</Label>
                      <p className="text-sm text-slate-400 mt-1">Enviar lembretes baseados em templates</p>
                    </div>
                    <input
                      type="checkbox"
                      checked={formData.auto_send_enabled}
                      onChange={(e) => setFormData({...formData, auto_send_enabled: e.target.checked})}
                      className="w-6 h-6"
                      data-testid="auto-send-toggle"
                    />
                  </div>

                  <div className="p-4 bg-zinc-800 rounded-lg">
                    <Label className="text-white">Horário de Envio</Label>
                    <div className="flex items-center gap-3 mt-2">
                      <Clock className="w-5 h-5 text-slate-400" />
                      <Input
                        type="time"
                        value={formData.send_time}
                        onChange={(e) => setFormData({...formData, send_time: e.target.value})}
                        className="w-40"
                        data-testid="send-time-input"
                      />
                      <span className="text-sm text-slate-400">(Horário de Brasília)</span>
                    </div>
                  </div>

                </div>

                <Button type="submit" className="w-full bg-violet-600 hover:bg-violet-700" data-testid="save-settings-btn">
                  <Settings className="w-4 h-4 mr-2" />
                  Salvar Configurações
                </Button>
              </form>
            </div>

            {/* Previsão de Envio - NOVA SEÇÃO */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white flex items-center gap-2">
                  <Users className="w-6 h-6 text-emerald-500" />
                  Próximos Envios
                </h2>
                <button 
                  onClick={() => { fetchPreview(); fetchQueue(); }} 
                  className="flex items-center gap-2 text-sm text-emerald-500 hover:text-emerald-400 transition-colors"
                >
                  <RefreshCw className="w-4 h-4" />
                  Atualizar
                </button>
              </div>
              
              {preview && preview.enabled && (
                <div className="mb-4 p-3 bg-emerald-900/20 border border-emerald-700 rounded-lg">
                  <p className="text-sm text-emerald-300">
                    📅 Próximo envio programado para <strong>{preview.send_time}</strong> (horário de Brasília).
                    {preview.total_clients > 0 && (
                      <span> {preview.total_clients} cliente(s) receberão mensagens.</span>
                    )}
                  </p>
                </div>
              )}

              {!preview || !preview.enabled ? (
                <div className="text-center py-8">
                  <Ban className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400">{preview?.message || 'Automação desabilitada'}</p>
                </div>
              ) : preview.clients.length === 0 ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
                  <p className="text-slate-400">Nenhum cliente para notificar hoje</p>
                  <p className="text-sm text-slate-500 mt-2">
                    {preview.message || 'Todos os envios programados já foram realizados ou não há clientes elegíveis'}
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {preview.clients.map((client) => (
                    <div 
                      key={client.id} 
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <p className="text-white font-medium">{client.name}</p>
                          {getStatusBadge(client.days_diff)}
                          {client.group_name && (
                            <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-violet-500/10 text-violet-400 border border-violet-500/30">
                              {client.group_name}
                            </span>
                          )}
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-xs text-slate-400">
                            📱 {client.phone}
                          </p>
                          <p className="text-xs text-slate-400">
                            📅 Vencimento: {new Date(client.payment_date).toLocaleDateString('pt-BR')}
                          </p>
                          <p className="text-xs text-slate-400">
                            💬 {client.reason}
                          </p>
                          <p className="text-xs text-slate-400">
                            📝 Template: <span className="text-violet-400">{client.template_name}</span>
                            {client.template_days_before !== undefined && (
                              <span className="text-slate-500 ml-1">
                                (config: {client.template_days_before > 0 ? `${client.template_days_before} dias antes` : 
                                  client.template_days_before === 0 ? 'no dia' : 
                                  `${Math.abs(client.template_days_before)} dias após`})
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => excludeClient(client.id, client.name)}
                        disabled={excludingId === client.id}
                        className="ml-4 p-2 text-rose-500 hover:bg-rose-500/10 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                        title="Cancelar envio para este cliente"
                      >
                        {excludingId === client.id ? (
                          <RefreshCw className="w-5 h-5 animate-spin" />
                        ) : (
                          <>
                            <X className="w-5 h-5" />
                            <span className="text-xs hidden sm:inline">Cancelar</span>
                          </>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {preview && preview.clients && preview.clients.length > 0 && (
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-sm text-slate-400 text-center">
                    Total de {preview.clients.length} {preview.clients.length === 1 ? 'cliente' : 'clientes'} para notificar
                    {preview.excluded_count > 0 && (
                      <span className="text-rose-400"> • {preview.excluded_count} excluído(s) hoje</span>
                    )}
                  </p>
                </div>
              )}
            </div>

            {/* Fila de Envio em Progresso */}
            {queue.length > 0 && (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 sm:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-white flex items-center gap-2">
                    <ListOrdered className="w-6 h-6 text-amber-500" />
                    Enviando Agora
                  </h2>
                </div>
                
                <div className="mb-4 p-3 bg-amber-900/20 border border-amber-700 rounded-lg">
                  <p className="text-sm text-amber-300">
                    ⏳ Mensagens sendo enviadas neste momento. Aguarde a conclusão.
                  </p>
                </div>

                <div className="space-y-3">
                  {queue.map((msg) => (
                    <div 
                      key={msg.id} 
                      className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-amber-700/30"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="text-white font-medium truncate">{msg.client_name}</p>
                          <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/30 animate-pulse">
                            Enviando...
                          </span>
                        </div>
                        
                        <div className="space-y-1">
                          <p className="text-xs text-slate-400">
                            📱 {msg.client_phone}
                          </p>
                          <p className="text-xs text-slate-400">
                            📝 Template: <span className="text-violet-400">{msg.template_name}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-zinc-800">
                  <p className="text-sm text-slate-400 text-center">
                    {queue.length} {queue.length === 1 ? 'mensagem' : 'mensagens'} em processamento
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
